import {
  Box,
  Card,
  CardActionArea,
  Stack,
  type StackProps,
  Typography,
  type TypographyProps,
  styled,
} from "@mui/material"
import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { MarkdownDisplay } from "../../../dataDisplay"
import { LinkTo, type LinkToProps, chevronHoverEffect } from "../../../navigation"

export interface NewsCardLayoutProps {
  Header: ReactNode
  title: string
  description?: string
  link: Pick<LinkToProps, "name" | "nameSx" | "chevronSize" | "containerSx"> & {
    to: NonNullable<LinkToProps["to"]>
  }
  sponsorChip?: {
    label: string
    sx?: TypographyProps["sx"]
  }
  stackSx?: StackProps["sx"]
}

export const NewsCardLayout = ({
  Header,
  title,
  description,
  link: { to, ...linkProps },
  sponsorChip,
  stackSx,
}: NewsCardLayoutProps) => {
  return (
    <Card
      sx={{
        position: "relative",
        overflow: "visible",
      }}
    >
      <CardActionArea
        component={Link}
        to={to}
        sx={{
          height: "100%",
          ...chevronHoverEffect,
        }}
      >
        {/* SPONSOR CHIP */}
        {sponsorChip && (
          <SponsorChip sx={sponsorChip.sx}>{sponsorChip.label}</SponsorChip>
        )}

        <Stack p={2} gap={2} sx={stackSx} height={"100%"}>
          {/* HEADER */}
          {Header}

          {/* CONTENT */}
          <Stack flex={1}>
            <Typography fontSize="0.9rem" fontWeight="bold" color="common.darkBlue">
              {title}
            </Typography>

            {description && (
              <Description mt={1}>
                <MarkdownDisplay ignoreFormatting>{description}</MarkdownDisplay>
              </Description>
            )}
          </Stack>

          {/* BOTTOM LINK */}
          <LinkTo {...linkProps} />
        </Stack>
      </CardActionArea>
    </Card>
  )
}

const Description = styled(Box)({
  fontSize: "0.75rem",
  color: "common.darkBlue",
  wordBreak: "break-word",
  overflow: "hidden",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  /* number of lines to show */
  WebkitLineClamp: "2",
  lineClamp: "2",
})

const SponsorChip = styled(Typography)(
  ({ theme: { palette, spacing, typography } }) => ({
    position: "absolute",
    top: typography.pxToRem(-18 / 2),
    left: spacing(2),
    borderRadius: "3px",
    padding: `0 ${spacing(0.5)}`,
    textTransform: "uppercase",
    lineHeight: typography.pxToRem(18),
    fontSize: typography.pxToRem(12),
    fontWeight: 500,
    color: palette.primary.main,
    backgroundColor: "white",
  }),
)
